// @ts-nocheck
const SECTION_SELECTOR = '[data-section-type="announcement-popup"]';
const STORAGE_PREFIX = 'announcement-popup-dismissed-';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getDismissKey = (sectionId) => `${STORAGE_PREFIX}${sectionId}`;

const isAutoOpenDismissed = (sectionId, durationHours) => {
  const raw = window.localStorage.getItem(getDismissKey(sectionId));
  if (!raw) return false;

  const dismissedAt = Number(raw);
  if (Number.isNaN(dismissedAt)) return false;

  const elapsed = (Date.now() - dismissedAt) / (1000 * 60 * 60);
  return elapsed < durationHours;
};

const saveAutoOpenDismissed = (sectionId) => {
  window.localStorage.setItem(getDismissKey(sectionId), String(Date.now()));
};

const formatCountdown = (time) => String(time).padStart(2, '0');

const initSection = (section) => {
  if (!(section instanceof HTMLElement)) return;
  if (section.dataset.announcementPopupInit === 'true') return;
  section.dataset.announcementPopupInit = 'true';

  const overlay = section.querySelector('.announcement-popup__overlay');
  const dialog = section.querySelector('.announcement-popup__dialog');
  const closeButton = section.querySelector('[data-announcement-close]');
  const triggers = Array.from(section.querySelectorAll('[data-announcement-open-modal]'));
  const slides = Array.from(section.querySelectorAll('.announcement-popup__slide'));
  const slideSpeed = Number(section.dataset.rotateSpeed) || 6;
  const autoOpenEnabled = section.dataset.autoOpenEnabled === 'true';
  const autoOpenDelay = Number(section.dataset.autoOpenDelay) || 4;
  const dismissDuration = Number(section.dataset.dismissDuration) || 24;

  if (!overlay || !dialog || !closeButton) return;

  const popupHeading = dialog.querySelector('[data-announcement-popup-heading]');
  const popupDescription = dialog.querySelector('[data-announcement-popup-description]');
  const imageWrapper = dialog.querySelector('[data-announcement-popup-image-wrapper]');
  const popupImage = dialog.querySelector('[data-announcement-popup-image]');
  const popupForm = dialog.querySelector('[data-announcement-popup-form]');
  const phoneInput = dialog.querySelector('[data-announcement-phone-input]');
  const formMessage = dialog.querySelector('[data-announcement-form-message]');

  let lastFocusedElement = null;
  let slideInterval = null;

  const showSlide = (index) => {
    slides.forEach((slide, i) => {
      slide.classList.toggle('announcement-popup__slide--active', i === index);
    });
  };

  const nextSlide = () => {
    if (slides.length < 2) return;
    const activeIndex = slides.findIndex((slide) => slide.classList.contains('announcement-popup__slide--active'));
    const nextIndex = activeIndex === -1 ? 0 : (activeIndex + 1) % slides.length;
    showSlide(nextIndex);
  };

  const prevSlide = () => {
    if (slides.length < 2) return;
    const activeIndex = slides.findIndex((slide) => slide.classList.contains('announcement-popup__slide--active'));
    const prevIndex = activeIndex <= 0 ? slides.length - 1 : activeIndex - 1;
    showSlide(prevIndex);
  };

  const getFocusableElements = () => {
    return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => element instanceof HTMLElement);
  };

  const setPopupContent = (trigger) => {
    if (!(trigger instanceof HTMLElement)) return;

    const heading = trigger.dataset.popupHeading || '';
    const descriptionTemplate = trigger.closest('.announcement-popup__slide')?.querySelector('.announcement-popup__popup-description-template');
    const imageSrc = trigger.dataset.popupImage || '';
    const buttonText = trigger.dataset.popupButtonText || '';
    const buttonLink = trigger.dataset.popupButtonLink || '';

    if (popupHeading) {
      popupHeading.textContent = heading;
    }

    if (popupDescription) {
      popupDescription.innerHTML = descriptionTemplate?.innerHTML || '';
    }

    if (imageWrapper && popupImage) {
      if (imageSrc) {
        popupImage.src = imageSrc;
        popupImage.alt = heading || 'Announcement image';
        imageWrapper.hidden = false;
      } else {
        imageWrapper.hidden = true;
    }
    }

    if (popupForm && phoneInput) {
      phoneInput.value = '';
      phoneInput.placeholder = trigger.dataset.popupPhonePlaceholder || phoneInput.placeholder || '';
      formMessage.textContent = '';
      formMessage.classList.remove('announcement-popup__form-message--error', 'announcement-popup__form-message--success');
    }
  };

  const validatePhone = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 7;
  };

  const showFormMessage = (message, type = 'error') => {
    if (!formMessage) return;
    formMessage.textContent = message;
    formMessage.classList.toggle('announcement-popup__form-message--error', type === 'error');
    formMessage.classList.toggle('announcement-popup__form-message--success', type === 'success');
  };

  const handleFormSubmit = (event) => {
    if (!popupForm || !phoneInput) return;
    event.preventDefault();

    const phone = phoneInput.value.trim();
    if (!phone) {
      showFormMessage('Please enter your phone number.', 'error');
      phoneInput.focus();
      return;
    }

    if (!validatePhone(phone)) {
      showFormMessage('Please enter a valid phone number.', 'error');
      phoneInput.focus();
      return;
    }

    window.localStorage.setItem(`announcement-popup-phone-${section.id}`, phone);
    showFormMessage('Thanks! We’ll contact you soon.', 'success');
    window.setTimeout(closePopup, 1200);
  };

  const openPopup = (trigger) => {
    if (section.classList.contains('announcement-popup--open')) return;
    if (trigger) {
      setPopupContent(trigger);
    }

    lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    section.classList.add('announcement-popup--open');
    dialog.classList.add('active');
    overlay.classList.add('active');
    dialog.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (phoneInput) {
      phoneInput.focus();
    } else {
      const focusable = getFocusableElements();
      if (focusable.length) {
        focusable[0].focus();
      } else {
        closeButton.focus();
      }
    }

    document.addEventListener('keydown', onKeydown);
  };

  const closePopup = () => {
    if (!section.classList.contains('announcement-popup--open')) return;

    section.classList.remove('announcement-popup--open');
    dialog.classList.remove('active');
    overlay.classList.remove('active');
    dialog.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);

    if (lastFocusedElement) {
      lastFocusedElement.focus();
    }

    saveAutoOpenDismissed(section.id);
  };

  const onKeydown = (event) => {
    if (event.key === 'Escape') {
      closePopup();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();
    if (focusable.length === 0) return;

    const firstElement = focusable[0];
    const lastElement = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const handleOverlayClick = () => closePopup();

  const setupCountdown = (slide) => {
    const trigger = slide.querySelector('[data-countdown-enabled]');
    if (!(trigger instanceof HTMLElement)) return;

    const enabled = trigger.dataset.countdownEnabled === 'true';
    if (!enabled) return;

    const timer = slide.querySelector('.announcement-popup__timer');
    if (!timer) return;

    const dateValue = trigger.dataset.countdownDate;
    const timeValue = trigger.dataset.countdownTime;
    const targetTime = new Date(`${dateValue}T${timeValue}`).getTime();
    if (Number.isNaN(targetTime)) return;

    const updateCountdown = () => {
      const now = Date.now();
      const difference = targetTime - now;
      if (difference <= 0) {
        timer.textContent = 'Expired';
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / (1000 * 60)) % 60);
      const seconds = Math.floor((difference / 1000) % 60);
      timer.textContent = `${days ? `${days}d ` : ''}${formatCountdown(hours)}h ${formatCountdown(minutes)}m ${formatCountdown(seconds)}s`;
    };

    updateCountdown();
    setInterval(updateCountdown, 1000);
  };

  const rotateSlides = () => {
    if (slides.length < 2) return;
    if (slideInterval) {
      clearInterval(slideInterval);
    }

    slideInterval = window.setInterval(() => {
      const activeIndex = slides.findIndex((slide) => slide.classList.contains('announcement-popup__slide--active'));
      const nextIndex = activeIndex === -1 ? 0 : (activeIndex + 1) % slides.length;
      showSlide(nextIndex);
    }, slideSpeed * 1000);
  };

  triggers.forEach((trigger) => {
    if (!(trigger instanceof HTMLElement)) return;
    trigger.addEventListener('click', () => openPopup(trigger));
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openPopup(trigger);
      }
    });
  });

  overlay.addEventListener('click', handleOverlayClick);
  closeButton.addEventListener('click', closePopup);
  if (popupForm) {
    popupForm.addEventListener('submit', handleFormSubmit);
  }
  dialog.addEventListener('click', (event) => event.stopPropagation());

  const navPrev = section.querySelector('[data-announcement-prev]');
  const navNext = section.querySelector('[data-announcement-next]');
  if (navPrev instanceof HTMLElement) {
    navPrev.addEventListener('click', () => {
      prevSlide();
      rotateSlides();
    });
  }

  if (navNext instanceof HTMLElement) {
    navNext.addEventListener('click', () => {
      nextSlide();
      rotateSlides();
    });
  }

  slides.forEach(setupCountdown);
  rotateSlides();

  if (autoOpenEnabled && !isAutoOpenDismissed(section.id, dismissDuration)) {
    window.setTimeout(() => openPopup(triggers[0]), autoOpenDelay * 1000);
  }
};

const initAllSections = () => {
  const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
  sections.forEach((section) => {
    if (section instanceof HTMLElement) {
      initSection(section);
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllSections);
} else {
  initAllSections();
}

window.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  if (event.target.matches(SECTION_SELECTOR)) {
    initSection(event.target);
    return;
  }

  const section = event.target.querySelector(SECTION_SELECTOR);
  if (section instanceof HTMLElement) {
    initSection(section);
  }
});
